import classNames from "classnames";
import FormLabel from "./FormLabel";
import Select from "react-select";
import "./selectWithFiltering.scss";
import TooltipComponent from "./TooltipComponent";
import ConditionalWrapper from "./ConditionalWrapper";
import { getOperatorOptionsForType } from "../../js/operators";

function SelectWithFiltering({
  actions,
  actionsClass = "d-flex justify-content-end",
  className,
  description,
  disabled = false,
  errors,
  label,
  isAdvancedSearchFilter = false,
  isClearable = true,
  isMulti = false,
  mandatory = false,
  name,
  onBlur,
  onChange,
  onOperatorChange,
  operator,
  operatorDisabled = false,
  optionGroups,
  options,
  placeholder = "",
  reverseLookup = false,
  showErrorsBeforeTouched = false,
  tooltipDescription,
  touched,
  value,
}) {
  const showInput =
    !isAdvancedSearchFilter ||
    (operator !== "containsdata" &&
      operator !== "doesnotcontaindata");

  const selectOptions = options
    ? options
        .filter((option) => !option.InActive) // remove inactive as these are only added to handle an existing value not in the options
        .map((option) => ({
          label: option.Value,
          value: reverseLookup ? option.Value : option.Key,
          key: option.Key,
        }))
    : optionGroups
    ? optionGroups.map((optionGroup) => ({
        label: optionGroup.Key,
        options: optionGroup.Value.map((v, i) => ({
          label: v.Value,
          value: reverseLookup ? v.Value : v.Key,
          key: v.Key,
        })),
      }))
    : [];

  const findMatchingValueInSelectOptions = () => {
    if (isMulti) {
      if (options) {
        return (
          selectOptions.filter(
            (s) =>
              s.label &&
              value &&
              value.some((v) => String(v) === String(s.key)) //If s has a label then it is assumed it has a value in its key
          ) || //This is to avoid the potential issue where s.key = 0 which means it wouldn't count it in the filter
          []
        );
      } else if (optionGroups) {
        //Loop through each option group and run a filter on each of the groups options
        //to get any matching records to the given value array and have an array of matching records
        let result = [];
        selectOptions.forEach((group) => {
          if (group.options && group.options.length > 0) {
            result = [
              ...result,
              ...group.options.filter(
                (s) =>
                  s.label &&
                  value &&
                  value.some((v) => String(v) === String(s.key))
              ),
            ];
          }
        });
        return result;
      } else {
        return [];
      }
    } else {
      if (options) {
        return (
          selectOptions.find(
            (o) => String(o.key) === String(value)
          ) || null
        );
      } else if (optionGroups) {
        var matchedOption = null;
        //Loop through each option group and run a find on the groups options to
        //find the matching option based on the given value
        selectOptions.forEach((group) => {
          if (
            !matchedOption &&
            group.options &&
            group.options.length > 0
          ) {
            matchedOption =
              group.options.find(
                (o) => String(o.key) === String(value)
              ) || null;
          }
        });
        return matchedOption;
      } else {
        return null;
      }
    }
  };

  let setValue =
    value !== undefined && value !== null && String(value)
      ? findMatchingValueInSelectOptions()
      : null;

  let activeOptionText = "";
  if (
    setValue &&
    options &&
    options.some((o) => o.Key === value) &&
    options.filter((o) => o.Key === value)[0].InActive
  ) {
    activeOptionText = "Selected " + label + " is inactive.";
  }
  if (reverseLookup && value && !setValue) {
    // if using reverseLookup and no match was found on the key (i.e. initial load)
    // then try to match using the value
    setValue =
      selectOptions.find((o) => String(o.value) === String(value)) ||
      null;
  }

  const wrapperId = `${name}-wrapper`;

  return (
    <>
      {label && (
        <div className="d-flex">
          <FormLabel
            className={"form-label me-auto"}
            label={label}
            mandatory={mandatory}
            name={name}
          />
          {actions && <div className={actionsClass}>{actions}</div>}
        </div>
      )}
      <ConditionalWrapper
        condition={isAdvancedSearchFilter}
        className="d-flex gap-3"
      >
        {isAdvancedSearchFilter && (
          <SelectWithFiltering
            isClearable={false}
            disabled={operatorDisabled}
            onChange={(event) => onOperatorChange(event.value)}
            options={getOperatorOptionsForType("select")}
            value={operator}
          />
        )}
        {showInput && (
          <div>
            <div
              className={classNames(
                className,
                "form-control searchable_select",
                touched || showErrorsBeforeTouched
                  ? errors
                    ? "is-invalid"
                    : value
                    ? "is-valid"
                    : ""
                  : ""
              )}
            >
              <Select
                className={"custom-select"}
                classNamePrefix="select"
                id={wrapperId}
                inputId={name}
                isMulti={isMulti}
                name={name}
                onChange={onChange}
                onBlur={onBlur}
                isDisabled={disabled}
                isSearchable={true}
                isClearable={isClearable && !mandatory}
                options={selectOptions}
                placeholder={placeholder}
                value={setValue}
              />
            </div>
            {description && (
              <div className="form-text d-flex jusify-content-between">
                <div>
                  {description}
                  {activeOptionText && <p>{activeOptionText}</p>}
                </div>
                {tooltipDescription && (
                  <TooltipComponent
                    name={name}
                    tooltipDescription={tooltipDescription}
                  />
                )}
              </div>
            )}
            {errors && (touched || showErrorsBeforeTouched) && (
              <div className="invalid-feedback">{errors}</div>
            )}
          </div>
        )}
      </ConditionalWrapper>
    </>
  );
}

export default SelectWithFiltering;
